"use client"

import { useState, useEffect, useRef, useCallback, useMemo } from "react"
import { Typography, Spin, notification, Modal, Button } from "antd"
import { useSearchParams } from "react-router-dom"
import { useChatContext } from "../contexts/ChatContext"
import { PlusOutlined, InfoCircleOutlined, MoreOutlined, CheckCircleFilled, DownloadOutlined } from "@ant-design/icons"
import assetService from "../services/assetService"
import { getCurrentUser } from "../services/authService"
import useAppMessage from "../hooks/useAppMessage"
import { handleCopy } from "../utils/common"
import semiFinalImg from "../styles/images/semi_final.png"
import final1Img from "../styles/images/final_1.png"
import final2Img from "../styles/images/final_2.png"
import final3Img from "../styles/images/final_3.png"
import final4Img from "../styles/images/final_4.png"
import final5Img from "../styles/images/final_5.png"
import rocketIcon from "../styles/images/icon_rocket.svg"
import certificateIcon from "../styles/images/icon_certificate.svg"
import aiAgentIcon from "../styles/images/icon_ai_agent.svg"
import bgCertificateActive from "../styles/images/bg_certificate_active.svg"
import bgAiAgentActive from "../styles/images/bg_ai_agent_active.svg"
import watermarkCertificate from "../styles/images/watermark_certificate.svg"
import watermarkAiAgent from "../styles/images/watermark_ai_agent.svg"
import maskRoundedSlant from "../assets/mask-rounded-slant.svg"
import ClipRoundedSlantDefs from "../assets/ClipRoundedSlantDefs"
import useAssetsPageStyles from "../styles/pages/AssetsPage"

const { Title } = Typography

const MASK_SVG_CERT = maskRoundedSlant
const MASK_SVG_AGENT = MASK_SVG_CERT

const CERTIFICATE_AWARD_IMAGES = {
  semi_final: semiFinalImg,
  final_1: final1Img,
  final_2: final2Img,
  final_3: final3Img,
  final_4: final4Img,
  final_5: final5Img
}

const getAwardImage = (awardType) => {
  if (!awardType) return null
  const key = awardType.toLowerCase()
  return CERTIFICATE_AWARD_IMAGES[key] || null
}

const AssetsPage = () => {
  const { styles } = useAssetsPageStyles()
  const [searchParams, setSearchParams] = useSearchParams()
  const { isChatOpen } = useChatContext()
  const [loading, setLoading] = useState(false)
  const { message, contextHolder } = useAppMessage()
  
  // 从URL参数获取userId和tab
  const targetUserId = searchParams.get('userId')
  const urlTab = searchParams.get('tab')
  const certificateIdParam = searchParams.get('certificateId')
  const certificateUrlParam = searchParams.get('certificateUrl')

  const currentUser = useMemo(() => getCurrentUser(), [])
  const currentUserId = currentUser?.id != null ? String(currentUser.id) : null
  const normalizedTargetUserId = targetUserId != null ? String(targetUserId) : null
  const isSelfViewing = !normalizedTargetUserId || (currentUserId && currentUserId === normalizedTargetUserId)
  
  // Tab状态 - 从URL参数读取默认tab
  const [activeTab, setActiveTab] = useState("my-assets")
  
  // 统计Tab状态 - 从URL参数读取默认tab，或默认certificates
  const [activeStatTab, setActiveStatTab] = useState(urlTab || "certificates")
  
  // 数据状态
  const [statsData, setStatsData] = useState({
    certificates: 0,
    computing: 0
  })
  const [certificates, setCertificates] = useState([])
  const [computingResources, setComputingResources] = useState([])
  
  // 分页状态 - 改为无限滚动模式
  const [certificatesPage, setCertificatesPage] = useState(1)
  const [computingPage, setComputingPage] = useState(1)
  const [certificatesHasMore, setCertificatesHasMore] = useState(true)
  const [computingHasMore, setComputingHasMore] = useState(true)
  
  // 滚动加载相关
  const certificatesObserver = useRef()
  const computingObserver = useRef()
  
  // Modal状态
  const [certificateModalVisible, setCertificateModalVisible] = useState(false)
  const [selectedCertificate, setSelectedCertificate] = useState(null)

  // 获取资产统计数据
  const fetchAssetStats = async () => {
    try {
      setLoading(true)
      const stats = await assetService.getAssetStats(targetUserId)
      setStatsData(stats)
    } catch (error) {
      console.error('获取资产统计失败:', error)
      notification.error({
        message: '获取资产统计失败',
        description: error.message || '请稍后重试'
      })
    } finally {
      setLoading(false)
    }
  }

  // 获取证书列表
  const fetchCertificates = async (page = 1, append = false) => {
    try {
      setLoading(true)
      const params = {
        pagination: {
          page: page,
          per_page: 6
        },
        userId: targetUserId
      }
      const response = await assetService.getCertificates(params)
      const newCertificates = response.certificates || []
      if (append) {
        setCertificates(prev => [...prev, ...newCertificates])
      } else {
        setCertificates(newCertificates)
      }
      
      // 判断是否还有更多数据
      if (response.pagination) {
        const { page: currentPage, total_pages } = response.pagination
        setCertificatesHasMore(currentPage < total_pages)
    } else {
        setCertificatesHasMore(newCertificates.length === 6)
      }
    } catch (error) {
      console.error('获取证书列表失败:', error)
      notification.error({
        message: '获取证书列表失败',
        description: error.message || '请稍后重试'
      })
    } finally {
      setLoading(false)
    }
  }
  
  // 获取算力资源列表
  const fetchComputingResources = async (page = 1, append = false) => {
    try {
      setLoading(true)
      const params = {
        pagination: {
          page: page,
          per_page: 10
        },
        userId: targetUserId
      }
      const response = await assetService.getComputingResources(params)
      const newResources = response.resources || []
      
      if (append) {
        setComputingResources(prev => [...prev, ...newResources])
      } else {
        setComputingResources(newResources)
      }
      
      // 判断是否还有更多数据
      if (response.pagination) {
        const { page: currentPage, total_pages } = response.pagination
        setComputingHasMore(currentPage < total_pages)
      } else {
        setComputingHasMore(newResources.length === 10)
      }
    } catch (error) {
      console.error('获取算力资源列表失败:', error)
      notification.error({
        message: '获取算力资源列表失败',
        description: error.message || '请稍后重试'
      })
    } finally {
      setLoading(false)
    }
  }
  
  // 证书滚动加载逻辑
  const lastCertificateRef = useCallback(
    (node) => {
      if (loading) return
      if (certificatesObserver.current) certificatesObserver.current.disconnect()
      certificatesObserver.current = new window.IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && certificatesHasMore) {
          setCertificatesPage((prev) => prev + 1)
          setLoading(true)
        }
      })
      if (node) certificatesObserver.current.observe(node)
    },
    [loading, certificatesHasMore]
  )

  // 算力滚动加载逻辑
  const lastComputingRef = useCallback(
    (node) => {
      if (loading) return
      if (computingObserver.current) computingObserver.current.disconnect()
      computingObserver.current = new window.IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && computingHasMore) {
          setComputingPage((prev) => prev + 1)
          setLoading(true)
        }
      })
      if (node) computingObserver.current.observe(node)
    },
    [loading, computingHasMore]
  )

  // 处理统计Tab切换
  const handleStatTabChange = (tabKey) => {
    setActiveStatTab(tabKey)
    if (tabKey === 'certificates') {
      setCertificatesPage(1)
      setCertificates([])
      setCertificatesHasMore(true)
      setLoading(true)
    } else if (tabKey === 'computing') {
      setComputingPage(1)
      setComputingResources([])
      setComputingHasMore(true)
      setLoading(true)
    }
  }

  // 处理证书点击
  const handleCertificateClick = (certificate) => {
    if (!isSelfViewing) {
      notification.warning({
        message: '无权限查看',
        description: '您只能查看自己的证书详情'
      })
      return
    }

    setSelectedCertificate(certificate)
    setCertificateModalVisible(true)
  }

  // 关闭证书详情Modal
  const handleCloseCertificateModal = () => {
    setCertificateModalVisible(false)
    setSelectedCertificate(null)
  }

  // 下载证书
  const handleDownloadCertificate = async (certificate) => {
    if (!certificate.fileId) {
      notification.warning({
        message: '证书文件不存在',
        description: '该证书没有关联的文件'
      })
      return
    }

    try {
      setLoading(true)
      
      // 调用服务层下载方法
      const blob = await assetService.downloadCertificate(certificate.fileId)
      
      // 创建下载链接并触发下载
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${certificate.title || 'certificate'}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
      
      notification.success({
        message: '下载成功',
        description: '证书已成功下载'
      })
    } catch (error) {
      console.error('下载证书失败:', error)
      notification.error({
        message: '下载失败',
        description: error.message || '请稍后重试'
      })
    } finally {
      setLoading(false)
    }
  }

  // 监听URL参数变化并切换Tab
  useEffect(() => {
    if (urlTab) {
      setActiveStatTab(urlTab)
    }
  }, [urlTab])

  // 初始化数据
  useEffect(() => {
    fetchAssetStats()
  }, [])

  // 监听证书页面变化
  useEffect(() => {
    if (certificatesPage > 1) {
      fetchCertificates(certificatesPage, true)
    }
  }, [certificatesPage])

  // 监听算力页面变化
  useEffect(() => {
    if (computingPage > 1) {
      fetchComputingResources(computingPage, true)
    }
  }, [computingPage])

  // 监听Tab切换
  useEffect(() => {
    if (activeStatTab === 'certificates' && certificatesPage === 1) {
      fetchCertificates(1, false)
    } else if (activeStatTab === 'computing' && computingPage === 1) {
      fetchComputingResources(1, false)
    }
  }, [activeStatTab])

  // 根据URL参数打开指定证书详情
  useEffect(() => {
    if (!certificateIdParam && !certificateUrlParam) {
      return
    }

    if (activeStatTab !== 'certificates') {
      setActiveStatTab('certificates')
    }

    if (!certificates || certificates.length === 0) {
      return
    }

    const matchedCertificate = certificates.find((certificate) => {
      if (certificateIdParam && String(certificate.id) === String(certificateIdParam)) {
        return true
      }
      if (certificateUrlParam && certificate.image === certificateUrlParam) {
        return true
      }
      return false
    })

    if (!matchedCertificate) {
      return
    }

    if (!certificateModalVisible || selectedCertificate?.id !== matchedCertificate.id) {
      setSelectedCertificate(matchedCertificate)
      setCertificateModalVisible(true)
    }

    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete('certificateId')
    nextParams.delete('certificateUrl')
    setSearchParams(nextParams, { replace: true })
  }, [
    certificateIdParam,
    certificateUrlParam,
    certificates,
    activeStatTab,
    certificateModalVisible,
    selectedCertificate,
    searchParams,
    setSearchParams
  ])

  // 统计卡片组件（Tab形式）
  const StatCard = ({ title, value, bgImage, bgColor, watermark, icon, isActive, onClick, maskDataUrl, tabKey }) => {
    const variant = tabKey === 'certificates' ? 'certificate' : 'computing'
    const rootClass = `${styles.statCardBase} ${isActive ? styles[`${variant}Active`] : styles[`${variant}Inactive`]}`
    return (
      <button
        onClick={onClick}
        className={`${rootClass} ${isActive ? `${styles.statMaskImage} ${styles.statMaskActive} ${styles.statClipActive}` : ''}`}
        style={isActive ? { ['--mask-image']: `url(${maskDataUrl || bgImage})` } : undefined}
      >
      {isActive && (
        <>
          <img 
            src={watermark} 
            alt="watermark" 
            className={styles.watermarkImage}
          />
        </>
      )}
      <div className="flex items-center justify-between h-full relative z-10">
        <div className={`flex flex-col justify-center h-full items-start space-y-2 ${isActive ? '-ml-3' : '-ml-1.5'}`}>
          <span className={`${isActive ? 'assets-stat-title-active' : 'assets-stat-title-inactive'}`}>
            {title}
          </span>
          {isActive && (
            <span className="assets-stat-value">
              {value}
            </span>
          )}
        </div>
        <div className="flex items-center space-x-1">
          {/* 图标 - 使用SVG图标 */}
          <div className="flex items-center justify-center">
             <img 
               src={icon} 
               alt="icon" 
               className={`object-contain transition-all duration-300 ${isActive ? 'w-[62px] h-[62px]' : 'w-8 h-8'}`}
             />
          </div>
        </div>
      </div>
      </button>
    )
  }

  // Tab切换组件
  const TabButton = ({ active, children, onClick }) => (
    <button
      onClick={onClick}
      className={`px-12 py-1 rounded-full text-base font-normal transition-colors ${
        active 
          ? 'text-white' 
          : 'text-gray-400'
      }`}
      style={{ 
        backgroundColor: active ? 'rgba(255, 255, 255, 0.1)' : 'transparent'
      }}
    >
      {children}
    </button>
  )

  // 证书卡片组件
  const CertificateCard = ({ certificate, onClick }) => {
    const awardImage = getAwardImage(certificate.awardType)

    return (
      <div 
        className="bg-[#2a2a2a] border border-[#3a3a3a] rounded-2xl p-4 w-full hover:shadow-lg transition-shadow duration-300 cursor-pointer relative"
        onClick={() => onClick(certificate)}
      >
        {/* 右上角标签 */}
        <div className="absolute top-2 -right-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full px-2 py-1 shadow-md z-10">
          <span className="text-xs text-white font-medium">燕缘·滴水湖</span>
        </div>

        <div className="flex flex-col">
          {/* 证书缩略图 */}
          <div className="w-full h-52 mb-3 rounded-lg overflow-hidden bg-[#1f1f1f] flex items-center justify-center">
            {awardImage ? (
              <img
                src={awardImage}
                alt="证书缩略图"
                className="w-full h-full object-contain"
              />
            ) : (
              <span className="text-4xl">📄</span>
            )}
          </div>

          {/* 证书信息 */}
          <div className="flex flex-col space-y-2">
            <h3 className="text-sm font-medium text-white truncate">
              {certificate.title}
            </h3>
            <p className="text-xs text-gray-400 line-clamp-2">
              {certificate.description}
            </p>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">
                {certificate.date}
              </span>
              <div className="flex items-center space-x-1">
                <CheckCircleFilled className="text-green-500 text-sm" />
                <span className="text-xs text-green-600">已认证</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // 算力卡片组件（AI Agent）
  const handleCopyAgentKey = async (agentId) => {
    if (!agentId) {
      return
    }
    try {
      const realKey = await assetService.getAgentKeyByAgentId(agentId)
      if (realKey) {
        handleCopy(realKey, message)
      } else {
        window.message?.warning?.('无法获取Agent Key，可能是权限不足')
      }
    } catch (err) {
      window.message?.error?.('复制失败，请稍后重试')
    }
  }

  const ComputingCard = ({ resource, showCopyButton, onCopyAgentKey }) => {
    // 计算有效期状态
    const isExpired = resource.expires_at && new Date(resource.expires_at) < new Date()
    const isActive = resource.status === 'published' && !isExpired
    const agentIdForCopy = resource?.id
    
    return (
      <div
        className="rounded-lg p-5 w-full relative overflow-hidden assets-computing-card"
      >
        {/* 右上角标签 */}
        <div className="absolute top-2 -right-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full px-2 py-1 shadow-md z-10">
          <span className="text-xs text-white font-medium">燕缘·滴水湖</span>
        </div>
        <div className="assets-computing-decor-circle" />
        
        {/* 头部信息 */}
        <div className="flex justify-between items-start mb-3">
          <div className="flex items-center space-x-2">
            <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center shadow-sm">
              <span className="text-lg">🤖</span>
            </div>
            <div className="flex flex-col">
              <div className="flex items-center space-x-1">
                <span className="text-sm text-gray-900 font-normal">{resource.name}</span>
              </div>
              <div className="flex items-center space-x-1 mt-1">
                <span className="text-xs text-gray-600 font-normal mr-2">{resource.description}</span>
                <div className="bg-gray-100 rounded-xl px-2 py-0.5 shadow-sm">
                  <span className="text-xs text-gray-700 font-normal">{resource.tag}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="w-4 h-4 bg-transparent flex items-center justify-center">
            <MoreOutlined className="text-gray-500" />
          </div>
        </div>

        {/* 有效期信息 */}
        <div className="space-y-1.5 mb-3">
          {resource.expires_at && (
            <div className="text-xs text-gray-500">
              有效期至：{new Date(resource.expires_at).toLocaleDateString('zh-CN')}
            </div>
          )}
        </div>

        {/* 状态与复制按钮 */}
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center space-x-1">
            {isActive ? (
              <>
                <CheckCircleFilled className="text-green-500 text-sm" />
                <span className="text-xs text-green-600">可用</span>
              </>
            ) : (
              <>
                <CheckCircleFilled className="text-gray-400 text-sm" />
                <span className="text-xs text-gray-500">不可用</span>
              </>
            )}
          </div>
          {showCopyButton && agentIdForCopy && (
            <Button
              type="link"
              size="small"
              className="px-0 text-xs"
              onClick={(e) => {
                e.stopPropagation()
                onCopyAgentKey?.(agentIdForCopy)
              }}
            >
              复制Agent Key
            </Button>
          )}
        </div>
      </div>
    )
  }

  
  return (
    <div className={`min-h-screen assets-page-bg ${isChatOpen ? 'mr-96' : ''}`}>
      {contextHolder}
      <ClipRoundedSlantDefs />
      <div className="max-w-4xl mx-auto px-4 py-8">
         {/* 顶部Tab切换和添加按钮 */}
         <div className="flex items-center justify-between mb-8">
           <div className="flex items-center space-x-3">
             <TabButton 
               active={activeTab === "my-assets"} 
               onClick={() => setActiveTab("my-assets")}
             >
               <div className="flex items-center space-x-2">
                 <img src={rocketIcon} alt="rocket" className="w-6 h-6" />
                 <span className="font-bold">我的资产</span>
               </div>
             </TabButton>
             {/* <TabButton 
               active={activeTab === "options"} 
               onClick={() => setActiveTab("options")}
             >
               选项
             </TabButton> */}
           </div>
           {/* <button 
             className="border rounded-3xl px-3 py-1 text-white text-sm font-normal transition-colors"
             style={{ 
               backgroundColor: '#E552B5',
               borderColor: '#E552B5'
             }}
           >
             添加算力
           </button> */}
         </div>

        {/* 统计卡片区域 */}
        <div className="flex items-end space-x-4 mb-8 w-2/3">
          <StatCard 
            title="证书" 
            value={`${statsData.certificates}张`} 
            bgImage={bgCertificateActive}
            bgColor="#74FFBB"
            watermark={watermarkCertificate}
            icon={certificateIcon}
            tabKey="certificates"
            isActive={activeStatTab === "certificates"}
            maskDataUrl={MASK_SVG_CERT}
            onClick={() => handleStatTabChange("certificates")}
          />
          <StatCard 
            title="Ai/Agent" 
            value={`${statsData.computing}条`} 
            bgImage={bgAiAgentActive}
            bgColor="#41FAF4"
            watermark={watermarkAiAgent}
            icon={aiAgentIcon}
            tabKey="computing"
            isActive={activeStatTab === "computing"}
            maskDataUrl={MASK_SVG_AGENT}
            onClick={() => handleStatTabChange("computing")}
          />
        </div>


         {/* 内容区域 - 根据选择的Tab显示不同内容 */}
         {activeStatTab === "certificates" ? (
           /* 证书列表 */
           <div>
             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
               {certificates.map((certificate, idx) => {
                 // 如果是最后一个元素，添加ref用于滚动加载
                 const isLast = idx === certificates.length - 1
                 return (
                   <div
                     key={certificate.id}
                     ref={isLast ? lastCertificateRef : null}
                   >
                     <CertificateCard 
                       certificate={certificate} 
                       onClick={handleCertificateClick}
                     />
                   </div>
                 )
               })}
             </div>
             {loading && (
               <div className="flex justify-center items-center py-8">
                 <Spin size="large" />
               </div>
             )}
             {!loading && certificates.length === 0 && (
               <div className="text-center text-gray-500 py-20">
                 暂无证书数据
               </div>
             )}
             {!certificatesHasMore && certificates.length > 0 && (
               <div className="text-center text-gray-500 py-4">
                 没有更多了
               </div>
             )}
           </div>
         ) : (
           /* 算力卡片列表 */
           <div>
             <div className="space-y-4">
               {computingResources.map((resource, idx) => {
                 // 如果是最后一个元素，添加ref用于滚动加载
                 const isLast = idx === computingResources.length - 1
                 return (
                   <div
                     key={resource.id}
                     ref={isLast ? lastComputingRef : null}
                   >
                     <ComputingCard
                       resource={resource}
                       showCopyButton={isSelfViewing}
                       onCopyAgentKey={handleCopyAgentKey}
                     />
                   </div>
                 )
               })}
             </div>
             {loading && (
               <div className="flex justify-center items-center py-8">
                 <Spin size="large" />
               </div>
             )}
             {!loading && computingResources.length === 0 && (
               <div className="text-center text-gray-500 py-20">
                 暂无算力数据
               </div>
             )}
             {!computingHasMore && computingResources.length > 0 && (
               <div className="text-center text-gray-500 py-4">
                 没有更多了
               </div>
             )}
           </div>
         )}

        {/* 证书详情Modal */}
        <Modal
          title="证书详情"
          open={certificateModalVisible}
          onCancel={handleCloseCertificateModal}
          footer={selectedCertificate && selectedCertificate.fileId ? (
            <Button 
              type="primary" 
              icon={<DownloadOutlined />}
              onClick={() => handleDownloadCertificate(selectedCertificate)}
              loading={loading}
              block
            >
              下载证书PDF
            </Button>
          ) : null}
          width={800}
          centered
        >
          {selectedCertificate && (
            <div className="space-y-6">
              {/* 证书图片 - 使用PDF缩略图组件 */}
              <div className="flex justify-center">
                <div className="w-full max-w-2xl rounded-lg overflow-hidden bg-gray-50" style={{ height: '420px' }}>
                  {(() => {
                    const awardImage = getAwardImage(selectedCertificate.awardType)

                    if (awardImage) {
                      return (
                        <img
                          src={awardImage}
                          alt="证书详情图片"
                          className="w-full h-full object-contain"
                        />
                      )
                    }

                    return (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-6xl">📄</span>
                      </div>
                    )
                  })()}
                </div>
              </div>
              
              {/* 证书信息 */}
              <div className="space-y-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    {selectedCertificate.title}
                  </h2>
                  <p className="text-gray-600">
                    {selectedCertificate.description}
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm font-medium text-gray-500">获得日期</span>
                    <p className="text-gray-900">{selectedCertificate.date}</p>
                  </div>
   
                  <div>
                    <span className="text-sm font-medium text-gray-500">状态</span>
                    <div className="flex items-center space-x-1">
                      <CheckCircleFilled className="text-green-500" />
                      <span className="text-green-600">已认证</span>
                    </div>
                  </div>
                </div>

                <div className="pt-2">
                  <span className="inline-flex items-center bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-medium rounded-full px-3 py-1 shadow-md">
                    燕缘·滴水湖
                  </span>
                </div>
                
                {/* 证书ID */}
                <div className="pt-4 border-t border-gray-200">
                  <span className="text-sm font-medium text-gray-500">证书ID</span>
                  <p className="text-gray-900 font-mono text-sm">{selectedCertificate.id}</p>
                </div>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </div>
  )
}

export default AssetsPage
